/** @jsxRuntime automatic */
/** @jsxImportSource react */
import type { ReactNode } from 'react';
import { CircleAlert, Inbox, RefreshCw, type LucideIcon } from 'lucide-react';
import { ApiError } from '../lib/api';
import { Button } from './Button';
import { Icon } from './Icon';

export interface SkeletonProps {
  /** A CSS width; the default fills the line. */
  width?: string;
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  round?: boolean;
  className?: string;
}

/** A grey placeholder in the shape of what is loading. Loading draws these in the final layout, never a bare spinner. */
export function Skeleton({ width, size = 'sm', round = false, className }: SkeletonProps) {
  return (
    <span
      className={['ui-sk', `ui-sk--${size}`, round ? 'is-round' : '', className ?? ''].filter(Boolean).join(' ')}
      // A computed width: the one inline style the kit allows.
      style={width ? { width } : undefined}
      aria-hidden="true"
    />
  );
}

export interface EmptyStateProps {
  icon?: LucideIcon;
  title: ReactNode;
  /** What goes here and how it gets here. */
  children?: ReactNode;
  /** The action that fills it, when there is one. */
  action?: ReactNode;
  compact?: boolean;
}

/** An empty list says what goes there and offers the action, instead of a grey sentence. */
export function EmptyState({ icon = Inbox, title, children, action, compact = false }: EmptyStateProps) {
  return (
    <div className={`ui-empty${compact ? ' is-compact' : ''}`}>
      <span className="ui-empty-icon"><Icon icon={icon} size="lg" /></span>
      <p className="ui-empty-title">{title}</p>
      {children ? <p className="ui-empty-body">{children}</p> : null}
      {action ? <div className="ui-empty-action">{action}</div> : null}
    </div>
  );
}

/** The service's own words for a failure, with its status when it answered one: `webhooks_unavailable (503)`. */
export function failureText(error: unknown, fallback = 'The request did not complete.'): string {
  const message = error instanceof Error && error.message.trim() ? error.message.trim() : fallback;
  const status = error instanceof ApiError && error.status ? ` (${error.status})` : '';
  return `${message.replace(/[.\s]+$/, '')}${status}.`;
}

export interface ErrorStateProps {
  /** What could not be done: "The overview could not be loaded". */
  title: ReactNode;
  /** The failure; its message is shown in the service's own words. */
  error: unknown;
  onRetry?: () => void;
  retrying?: boolean;
  /** A smaller version for inside a panel. */
  compact?: boolean;
  children?: ReactNode;
}

/**
 * A failed load named in the service's own words, with Try again. It replaces only the part that failed, so the rest
 * of the page keeps working.
 */
export function ErrorState({ title, error, onRetry, retrying = false, compact = false, children }: ErrorStateProps) {
  return (
    <div className={`ui-error${compact ? ' is-compact' : ''}`} role="alert">
      <span className="ui-error-icon"><Icon icon={CircleAlert} size="lg" /></span>
      <p className="ui-error-title">{title}</p>
      <p className="ui-error-body">The service said: {failureText(error)}</p>
      {children ? <div className="ui-error-body">{children}</div> : null}
      {onRetry ? (
        <div className="ui-error-action">
          <Button icon={RefreshCw} size="sm" onClick={onRetry} busy={retrying}>Try again</Button>
        </div>
      ) : null}
    </div>
  );
}
